import { useState, useEffect } from 'react';
import { Bell, BellOff, Clock } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

function NudgeSettings({ theme }) {
  const [enabled, setEnabled] = useState(false);
  const [hour, setHour] = useState(9);
  const [minute, setMinute] = useState(0);
  const [loading, setLoading] = useState(true);
  const [permissionGranted, setPermissionGranted] = useState(false);

  useEffect(() => {
    if ('Notification' in window) {
      setPermissionGranted(Notification.permission === 'granted');
    }
    loadPreferences();
  }, []);

  const loadPreferences = async () => {
    try {
      const res = await api.get('/api/notifications/preferences');
      setEnabled(res.data.nudge_enabled);
      setHour(res.data.nudge_hour);
      setMinute(res.data.nudge_minute);
    } catch {
      // defaults are fine
    } finally {
      setLoading(false);
    }
  };

  const requestPermission = async () => {
    if (!('Notification' in window)) {
      toast.error('Notifications not supported in this browser');
      return false;
    }
    const result = await Notification.requestPermission();
    const granted = result === 'granted';
    setPermissionGranted(granted);
    if (!granted) toast.error('Notification permission denied');
    return granted;
  };

  const handleToggle = async () => {
    const newEnabled = !enabled;
    if (newEnabled && !permissionGranted) {
      const granted = await requestPermission();
      if (!granted) return;
    }
    setEnabled(newEnabled);
    await savePreferences(newEnabled, hour, minute);
    if (newEnabled) {
      scheduleLocalNudge(hour, minute);
      toast.success(`Nudge set for ${formatTime(hour, minute)} daily`);
    } else {
      clearLocalNudge();
      toast.success('Daily nudge disabled');
    }
  };

  const handleTimeChange = async (newHour, newMinute) => {
    setHour(newHour);
    setMinute(newMinute);
    if (enabled) {
      await savePreferences(enabled, newHour, newMinute);
      scheduleLocalNudge(newHour, newMinute);
      toast.success(`Nudge time updated to ${formatTime(newHour, newMinute)}`);
    }
  };

  const savePreferences = async (nudgeEnabled, nudgeHour, nudgeMinute) => {
    try {
      await api.post('/api/notifications/preferences', {
        nudge_enabled: nudgeEnabled,
        nudge_hour: nudgeHour,
        nudge_minute: nudgeMinute,
      });
    } catch {
      toast.error('Failed to save notification preferences');
    }
  };

  const formatTime = (h, m) => {
    const period = h >= 12 ? 'PM' : 'AM';
    const displayH = h === 0 ? 12 : h > 12 ? h - 12 : h;
    return `${displayH}:${m.toString().padStart(2, '0')} ${period}`;
  };

  // Schedule a client-side nudge using setTimeout (works while tab is open)
  const scheduleLocalNudge = (h, m) => {
    clearLocalNudge();
    const now = new Date();
    const target = new Date();
    target.setHours(h, m, 0, 0);
    if (target <= now) target.setDate(target.getDate() + 1);
    const delay = target - now;

    const timerId = setTimeout(() => {
      if ('Notification' in window && Notification.permission === 'granted') {
        new Notification('Procrastination Popper', {
          body: "You left tasks unfinished. Pick just one — that's all it takes.",
          icon: '/logo192.png',
          tag: 'daily-nudge',
        });
      }
      // Reschedule for next day
      scheduleLocalNudge(h, m);
    }, delay);

    window.__nudgeTimerId = timerId;
  };

  const clearLocalNudge = () => {
    if (window.__nudgeTimerId) {
      clearTimeout(window.__nudgeTimerId);
      window.__nudgeTimerId = null;
    }
  };

  // On mount, auto-schedule if enabled
  useEffect(() => {
    if (!loading && enabled && permissionGranted) {
      scheduleLocalNudge(hour, minute);
    }
    return () => clearLocalNudge();
  }, [loading]);

  if (loading) return null;

  const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => i);

  return (
    <div
      className={`${theme?.colors.surface || 'bg-white'} border ${theme?.colors.border || 'border-stone-200'} rounded-xl p-4 sm:p-6 shadow-sm`}
      data-testid="nudge-settings"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Bell className={`w-5 h-5 ${theme?.colors.accent || 'text-[#E07A5F]'}`} />
          <h3
            className={`text-base sm:text-lg font-medium ${theme?.colors.textPrimary || 'text-stone-900'}`}
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            Daily Nudge
          </h3>
        </div>
        <button
          onClick={handleToggle}
          data-testid="nudge-toggle"
          className={`relative w-12 h-6 rounded-full transition-colors ${
            enabled ? 'bg-[#E07A5F]' : `${theme?.colors.bg || 'bg-stone-200'}`
          }`}
        >
          <div
            className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${
              enabled ? 'translate-x-6' : 'translate-x-0.5'
            }`}
          />
        </button>
      </div>

      {enabled && (
        <div className="space-y-3">
          <p
            className={`text-sm ${theme?.colors.textSecondary || 'text-stone-500'}`}
            style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
          >
            Get a gentle reminder to check your tasks
          </p>
          <div className="flex items-center gap-2">
            <Clock className={`w-4 h-4 ${theme?.colors.textSecondary || 'text-stone-400'}`} />
            <select
              value={hour}
              onChange={(e) => handleTimeChange(parseInt(e.target.value), minute)}
              data-testid="nudge-hour-select"
              className={`text-sm px-2 py-1 rounded-lg border ${theme?.colors.border || 'border-stone-200'} ${theme?.colors.textPrimary || 'text-stone-900'} bg-transparent`}
              style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
            >
              {HOUR_OPTIONS.map((h) => (
                <option key={h} value={h}>
                  {formatTime(h, 0).split(':')[0]} {h >= 12 ? 'PM' : 'AM'}
                </option>
              ))}
            </select>
            <span className={`text-sm ${theme?.colors.textSecondary || 'text-stone-400'}`}>:</span>
            <select
              value={minute}
              onChange={(e) => handleTimeChange(hour, parseInt(e.target.value))}
              data-testid="nudge-minute-select"
              className={`text-sm px-2 py-1 rounded-lg border ${theme?.colors.border || 'border-stone-200'} ${theme?.colors.textPrimary || 'text-stone-900'} bg-transparent`}
              style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
            >
              {[0, 15, 30, 45].map((m) => (
                <option key={m} value={m}>{m.toString().padStart(2, '0')}</option>
              ))}
            </select>
          </div>
          <p
            className={`text-xs ${theme?.colors.textSecondary || 'text-stone-400'}`}
            style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
          >
            Nudge at {formatTime(hour, minute)} &middot; {permissionGranted ? 'Notifications allowed' : 'Permission needed'}
          </p>
        </div>
      )}

      {!enabled && (
        <p
          className={`text-sm ${theme?.colors.textSecondary || 'text-stone-500'}`}
          style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
        >
          {enabled ? '' : 'A gentle daily reminder to tackle your unfinished tasks'}
        </p>
      )}
    </div>
  );
}

export default NudgeSettings;
